"use client";

import { Popover, Separator, ShipIcon } from "@pharaday/lighthouse";
import type { ReactNode } from "react";

import { MenuItem } from "../../components/menu-item";
import type { App } from "../../core/domain/entities/app";
import type { Tool } from "../../core/domain/entities/tool";
import { mapApplicationTypeToIcon } from "../../core/domain/enums/application-type.enum";
import { mapToolTypeToIcon, ToolTypeEnum } from "../../core/domain/enums/tool-type.enum";
import { useDismissOnNavigate } from "../../hooks/useDismissOnNavigate";

export type MenuProps = {
  children: ReactNode;
  appData?: App[];
  toolData?: Tool[];
};

const Menu = ({ children, appData, toolData }: MenuProps) => {
  const [open, setOpen] = useDismissOnNavigate();

  if (!appData || !toolData) {
    return null;
  }

  const hasApps = appData.length > 0;
  const hasTools = toolData.length > 0;

  if (!hasApps && !hasTools) {
    return null;
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Content className="flex w-full flex-col gap-5 px-4 py-6" align="end">
        {hasApps && (
          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-bold text-neutral-10">APPS</h3>
            <div className="grid w-full grid-cols-[repeat(3,8rem)] gap-x-1 gap-y-3">
              {appData.map(app => (
                <MenuItem
                  key={app.id}
                  Icon={mapApplicationTypeToIcon[app.type] ?? ShipIcon}
                  name={app.name}
                  description={app.description}
                  urlRoot={app.urlRoot || ""}
                  onClick={() => setOpen(false)}
                />
              ))}
            </div>
          </div>
        )}
        {hasApps && hasTools && <Separator />}
        {hasTools && (
          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-bold text-neutral-10">TOOLS</h3>
            <div className="grid w-full grid-cols-[repeat(3,8rem)] gap-x-1 gap-y-3">
              {toolData
                ?.filter(tool => tool.type !== ToolTypeEnum.ADMIN)
                .map(tool => (
                  <MenuItem
                    key={tool.id}
                    Icon={mapToolTypeToIcon[tool.type] ?? ShipIcon}
                    name={tool.name}
                    description={tool.description}
                    urlRoot={tool.urlRoot || ""}
                    onClick={() => setOpen(false)}
                  />
                ))}
            </div>
          </div>
        )}
      </Popover.Content>
    </Popover.Root>
  );
};

export default Menu;
